import { html, type TemplateResult } from 'lit';

import type { CancelConfirmView } from '../../types/views';

import './csv-import-cancel-confirm-body.js';

// ============================================================================
// Types
// ============================================================================

export interface CancelConfirmHandlers {
  onYes: (e: Event) => void;
  onNo: (e: Event) => void;
  onDownload: (e: Event) => void;
}

// ============================================================================
// Panel template function
// ============================================================================

/**
 * Inline cancel-confirmation panel: body component (slot=step-body) + footer
 * buttons (slot=footer). Rendered by the orchestrator in place of the active
 * step when confirmation is required. No modal chrome — the shell provides it.
 *
 * Buttons bind straight to orchestrator handlers; no intermediary events.
 */
export function cancelConfirmPanelTemplate(
  view: CancelConfirmView,
  { onYes, onNo, onDownload }: CancelConfirmHandlers,
): TemplateResult {
  return html`
    <csv-import-cancel-confirm-body
      slot="step-body"
      .view=${view}
    ></csv-import-cancel-confirm-body>
    ${renderFooter(view, onYes, onNo, onDownload)}
  `;
}

// ============================================================================
// Footer rendering
// ============================================================================

function renderFooter(
  view: CancelConfirmView,
  onYes: (e: Event) => void,
  onNo: (e: Event) => void,
  onDownload: (e: Event) => void,
): TemplateResult {
  switch (view) {
    case 'cancel-import':
      // Initial focus → "Yes, cancel the import" (first visible button per PO decision).
      return html`
        <inmar-hc-button
          slot="footer"
          label="Yes, cancel the import"
          variant="secondary"
          data-initial-focus
          @button-click=${onYes}
        ></inmar-hc-button>
        <inmar-hc-button
          slot="footer"
          label="No, continue importing"
          variant="secondary"
          @button-click=${onNo}
        ></inmar-hc-button>
      `;

    case 'download-report':
      // Initial focus → "Yes, close" (first visible button per PO decision).
      return html`
        <inmar-hc-button
          slot="footer"
          label="Yes, close"
          variant="secondary"
          data-initial-focus
          @button-click=${onYes}
        ></inmar-hc-button>
        <inmar-hc-button
          slot="footer"
          label="Download Final Error Report"
          variant="secondary"
          @button-click=${onDownload}
        >
          <hcds-icon
            slot="left-icon"
            name="download"
            family="classic"
            icon-style="light"
          ></hcds-icon>
        </inmar-hc-button>
      `;

    case 'download-report-success':
      // Single button — report already downloaded, leaving is safe.
      return html`
        <inmar-hc-button
          slot="footer"
          label="Close"
          variant="secondary"
          data-initial-focus
          @button-click=${onYes}
        ></inmar-hc-button>
      `;
  }
}
